// Services pour gérer les appels API
class ApiService {
  private baseUrl: string;
  
  // Routes publiques qui ne nécessitent pas de token
  private publicRoutes = [
    '/lots-technique',
    '/lots-departements', 
    '/departements',
    '/lots-technique/categories'
  ];

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '';
  }

  private isPublicRoute(endpoint: string): boolean {
    return this.publicRoutes.some(route => endpoint === route || endpoint.startsWith(route + '/'));
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const token = localStorage.getItem('token');
    const isPublic = this.isPublicRoute(endpoint);

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        // N'ajoute le token que si ce n'est pas une route publique
        ...(!isPublic && token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Erreur ${response.status}`);
    }

    return response.json();
  }

  // Méthodes spécifiques
  async getLotsTechniques() {
    const response = await this.makeRequest('/lots-technique');
    return response?.data || [];
  }

  async getLotsGeographiques() {
    const response = await this.makeRequest('/lots-departements');
    return response?.data || [];
  }

  async getDepartements() {
    const response = await this.makeRequest('/departements');
    return response?.data || [];
  }

  async createOperation(operationData: any) {
    const response = await this.makeRequest('/subsequents/operation', {
      method: 'POST',
      body: JSON.stringify(operationData),
    });
    return response?.data;
  }

  // Modifier cette méthode pour utiliser la bonne URL
  async createBonCommande(commandeData: any) {
    const response = await this.makeRequest('/bdc/operation', {
      method: 'POST',
      body: JSON.stringify(commandeData),
    });
    return response?.data;
  }

  // Departement du siege social de l'organisme de l'acheteur connecte. `null` si l'adresse n'a pas
  // de departement exploitable. Utilise par la page Catalogue pour pre-selectionner le lot
  // geographique (donc le titulaire dont on affiche les articles et les prix).
  async getSiegeDepartement(): Promise<{ id_departement: number; nom: string } | null> {
    const response = await this.makeRequest('/acheteur-profile/siege-departement');
    return response?.data ?? null;
  }

  private getEndpointByType(baseEndpoint: string, type: 'MS' | 'BC'): string {
    const endpoints = {
      'MS': baseEndpoint.replace('{type}', 'subsequents'),
      'BC': baseEndpoint.replace('{type}', 'bons-commande')
    };
    return endpoints[type];
  }

  async getCommandeDetails(id: string, type: 'MS' | 'BC') {
    const endpoint = type === 'MS'
      ? `/subsequents/commande/${id}`
      : `/bons-commande/${id}`;

    return this.makeRequest(endpoint);
  }

  async getOperationDetails(id: string, type: 'MS' | 'BC') {
    const endpoint = type === 'MS'
      ? `/subsequents/operation/${id}`
      : `/bons-commande/operation/${id}`;

    return this.makeRequest(endpoint);
  }

  // Synthese annuelle (onglet "Statistiques et besoins") - scopee a l'organisme du user connecte cote API
  async getSyntheseBesoin() {
    const response = await this.makeRequest('/synthese-besoin');
    return response?.data || null;
  }

  // Estimation financiere des besoins (onglet acheteur) - passe calcule + previsions saisies
  async getEstimationBesoins() {
    const response = await this.makeRequest('/estimation-besoins');
    return response?.data || null;
  }

  // Upsert d'une prevision (organisme x lot x annee) ; annee en cours ou future
  async saveEstimationPrevision(idLotTechnique: number, annee: number, montant: number) {
    const response = await this.makeRequest('/estimation-besoins/prevision', {
      method: 'PUT',
      body: JSON.stringify({ id_lot_technique: idLotTechnique, annee, montant }),
    });
    return response?.data || null;
  }

  // Localisation des besoins en % (onglet acheteur) - passe calcule (bdc_archive) + previsions
  async getLocalisationBesoins() {
    const response = await this.makeRequest('/localisation-besoins');
    return response?.data || null;
  }

  // Sauvegarde atomique d'une annee complete (colonne) : la repartition doit totaliser 100 %.
  // L'invariant est revalide cote serveur (cf. LocalisationBesoinController::updateAnnee).
  async saveLocalisationAnnee(annee: number, repartition: { id_departement: number; pourcentage: number }[]) {
    const response = await this.makeRequest('/localisation-besoins/annee', {
      method: 'PUT',
      body: JSON.stringify({ annee, repartition }),
    });
    return response?.data || null;
  }

  // Met a jour le perimetre geographique d'achat d'un organisme (national XOR zones).
  async saveOrganismePerimetre(
    organismeId: number,
    payload: { perimetre_type: 'national' | 'zones'; region_ids: number[]; departement_ids: number[] },
  ) {
    const response = await this.makeRequest(`/organismes/${organismeId}/perimetre`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return response?.data || null;
  }

  // Suggestion d'un nouveau lot technique (bouton "+ Autres" du tableau d'estimation)
  async suggererLot(payload: { nom_lot: string; justification?: string; montant_estime?: number | null }) {
    const response = await this.makeRequest('/estimation-besoins/suggestion', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || null;
  }

  // Evaluation de Centralis (onglet "Evaluations") - mes notes + satisfaction globale + cote
  async getEvaluationCentralis() {
    const response = await this.makeRequest('/evaluations/centralis');
    return response?.data || null;
  }

  // Upsert d'un critere de l'organisme/entite au clic d'etoile ; renvoie l'etat a jour (cote + notes)
  async rateEvaluationCentralis(critere: string, note: number) {
    const response = await this.makeRequest('/evaluations/centralis', {
      method: 'PUT',
      body: JSON.stringify({ critere, note }),
    });
    return response?.data || null;
  }

  // Affectation des membres sur les lots (carte) : contexte (cote + lots + affectables).
  async getAffectationContexte() {
    const response = await this.makeRequest('/equipe/affectation/contexte');
    return response?.data || null;
  }

  // Affectation courante des departements pour un lot technique.
  async getAffectationLot(lotTechniqueId: number) {
    const response = await this.makeRequest(`/equipe/affectation/lot/${lotTechniqueId}`);
    return response?.data || null;
  }

  // Affecte (ou desaffecte si affecte_id null) un lot d'un seul coup sur N departements.
  async batchAffecterLot(payload: { lot_technique_id: number; departement_ids: number[]; affecte_id: number | null }) {
    const response = await this.makeRequest('/equipe/affectation/batch', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response?.data || null;
  }

  // L'acheteur modifie note + commentaire de son avis.
  async updateAvisPrestataire(id: number, note: number, commentaire: string | null) {
    const response = await this.makeRequest(`/avis-prestataires/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ note, commentaire }),
    });
    return response?.data || null;
  }

  // Avis deposes par l'organisme de l'acheteur connecte (profil acheteur).
  async getAvisPrestataireDonnes() {
    const response = await this.makeRequest('/avis-prestataires/donnes');
    return response?.data || [];
  }

  // Avis recus par une entite (annuaire si entiteId fourni, sinon le prestataire connecte).
  async getAvisPrestataireRecus(entiteId?: number) {
    const q = entiteId ? `?entite_id=${entiteId}` : '';
    const response = await this.makeRequest(`/avis-prestataires/recus${q}`);
    return response?.data || null;
  }

  // Lecture seule d'un avis par commande (acheteur + prestataire). L'avis est cree en amont
  // (hook a la validation + backfill), jamais ici.
  async getAvisPrestataireCommande(typeCommande: 'BC' | 'MS', commandeId: number) {
    const response = await this.makeRequest(
      `/avis-prestataires/commande?type_commande=${typeCommande}&commande_id=${commandeId}`,
    );
    return response?.data || null;
  }

  // Moyenne + nombre d'avis par entite, en un appel (cartes annuaire). Map { entite_id: {nombre, moyenne} }.
  async getAvisPrestataireMoyennes(entiteIds: number[]): Promise<Record<number, { nombre: number; moyenne: number | null }>> {
    if (!entiteIds.length) return {};
    const response = await this.makeRequest(`/avis-prestataires/moyennes?entite_ids=${entiteIds.join(',')}`);
    return (response?.data as Record<number, { nombre: number; moyenne: number | null }>) || {};
  }

  // ── Avis prestataire -> acheteur (PRIVE) : miroir du systeme ci-dessus, visible des 2 parties
  // seules (acheteur note + prestataire auteur), jamais l'annuaire (pas de /moyennes). ──────────

  // Le prestataire (auteur) modifie note + commentaire de son avis sur l'acheteur.
  async updateAvisAcheteur(id: number, note: number, commentaire: string | null) {
    const response = await this.makeRequest(`/avis-acheteurs/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ note, commentaire }),
    });
    return response?.data || null;
  }

  // Avis deposes par l'entite (prestataire) de l'utilisateur connecte (profil prestataire).
  async getAvisAcheteurDonnes() {
    const response = await this.makeRequest('/avis-acheteurs/donnes');
    return response?.data || [];
  }

  // Avis recus par l'organisme (acheteur) de l'utilisateur connecte. PRIVE : restreint au seul
  // acheteur concerne cote serveur (pas de parametre). Renvoie { organisme_id, nombre, moyenne, avis }.
  async getAvisAcheteurRecus() {
    const response = await this.makeRequest('/avis-acheteurs/recus');
    return response?.data || null;
  }

  // Lecture seule d'un avis acheteur par commande (cree en amont, hook + backfill). Renvoie { avis, can_edit }.
  async getAvisAcheteurCommande(typeCommande: 'BC' | 'MS', commandeId: number) {
    const response = await this.makeRequest(
      `/avis-acheteurs/commande?type_commande=${typeCommande}&commande_id=${commandeId}`,
    );
    return response?.data || null;
  }
}

export const apiService = new ApiService();